import * as THREE from "three";
import { Input } from "../core/input";
import type { World } from "../world/world";
import { COBWEB, isSolid, WATER } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import { sound } from "../audio/sound-engine";
import { FoodStats } from "./food";

/** Player AABB, in blocks. Minecraft's canonical 0.6 x 1.8 footprint. */
export const PLAYER_WIDTH = 0.6;
export const PLAYER_HEIGHT = 1.8;
export const EYE_HEIGHT = 1.62;
/** Sneaking shrinks the collision box without shrinking the camera. */
export const SNEAK_HEIGHT = 1.5;
export const SNEAK_EYE_HEIGHT = 1.42;

export type CameraMode = "first-person" | "third-person-back" | "third-person-front";
export const CAMERA_MODES: readonly CameraMode[] = [
  "first-person",
  "third-person-back",
  "third-person-front",
];

export type CharacterSkin = "steve" | "alex";

const EPS = 1e-3;
/** Collision tests shrink the box slightly so a flush face isn't a false hit. */
const COLL_EPS = 1e-5;
/** Never move more than this per collision substep, so we never tunnel. */
const MAX_SUBSTEP = 0.4;

const GRAVITY = 30;
const JUMP_SPEED = 8.6;
const WALK_SPEED = 4.4;
const SPRINT_SPEED = 6.2;
const SNEAK_SPEED = 1.6;
const SWIM_SPEED = 3.2;
const FLY_SPEED = 12;
const FLY_SPRINT_SPEED = 28;
const GROUND_ACCEL = 60;
const AIR_ACCEL = 12;
/** Below this vertical speed while grounded, the player is considered standing. */
const TERMINAL_VELOCITY = 58;

/** Survival vitals, in Minecraft's units: hp is half-hearts, air is bubbles. */
export const MAX_HEALTH = 20;
export const MAX_AIR = 10;
/** Seconds without taking damage before health starts regenerating. */
const REGEN_DELAY = 8;
/** Seconds of no air between drowning ticks. */
const DROWN_INTERVAL = 1.25;
/** Falls shorter than this are free, as in Minecraft. */
const FALL_DAMAGE_THRESHOLD = 3;

/** Simple fixed-step accumulator so physics never depends on frame rate. */
const FIXED_DT = 1 / 120;
const MAX_STEPS = 8;

export class Player {
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();

  yaw = 0;
  pitch = 0;

  onGround = false;
  inWater = false;
  headInWater = false;
  flying = false;
  sprinting = false;
  sneaking = false;
  blocking = false;

  /** Survival rules: fall damage, drowning, no flight. Set by the game. */
  survival = false;
  /** Creative can fly; survival cannot. Double-tap Space / F stop working when false. */
  allowFlight = true;

  health = MAX_HEALTH;
  air = MAX_AIR;
  readonly foodStats = new FoodStats();
  /** Cause of the most recent damage, shown on the death screen. */
  lastCause = "took fatal damage";
  /** 0..1 red-flash strength for the HUD. */
  hurtFlash = 0;

  /** Flat [yaw, pitch] of the camera, for the renderer. */
  readonly forward = new THREE.Vector3();

  /** Camera perspective: first-person, third-person back, or third-person front. */
  cameraMode: CameraMode = "first-person";

  cycleCameraMode(): CameraMode {
    const currentIndex = CAMERA_MODES.indexOf(this.cameraMode);
    this.cameraMode = CAMERA_MODES[(currentIndex + 1) % CAMERA_MODES.length];
    this.syncCamera();
    return this.cameraMode;
  }

  /** Character model: Steve or Alex. */
  skin: CharacterSkin = "steve";

  cycleSkin(): CharacterSkin {
    this.skin = this.skin === "steve" ? "alex" : "steve";
    try {
      localStorage.setItem("voxelcraft:character", this.skin);
    } catch {}
    return this.skin;
  }

  private coyote = 0;
  private jumpBuffer = 0;
  private lastSpaceTap = -1;
  private height = PLAYER_HEIGHT;
  private accumulator = 0;
  private sinceDamage = Infinity;
  private drownTimer = 0;
  /** Highest y reached since last leaving the ground, for fall damage. */
  private fallPeak = 0;
  private stepDistance = 0;
  private swimTimer = 0;

  /** Configurable control and camera settings */
  mouseSensitivity = 1.0;
  baseFov = 75;
  fovEffects = true;

  /**
   * `camera` is owned by the renderer and merely driven from here — sharing one
   * instance keeps the projection settings (fov, aspect, far plane) authoritative
   * in the engine.
   */
  constructor(
    private readonly world: World,
    private readonly camera: THREE.PerspectiveCamera,
  ) {
    this.camera.rotation.order = "YXZ";
    try {
      const savedSkin = localStorage.getItem("voxelcraft:character");
      if (savedSkin === "steve" || savedSkin === "alex") {
        this.skin = savedSkin;
      }
    } catch {}
  }

  get eyeHeight(): number {
    return this.sneaking ? SNEAK_EYE_HEIGHT : EYE_HEIGHT;
  }

  /** Death stops movement until `respawn` brings the player back. */
  get isDead(): boolean {
    return this.health <= 0;
  }

  spawn(pos: THREE.Vector3): void {
    this.position.copy(pos);
    this.velocity.set(0, 0, 0);
    this.fallPeak = pos.y;
    this.syncCamera();
  }

  /** Full reset at the spawn point: used by the death screen. */
  respawn(pos: THREE.Vector3): void {
    this.spawn(pos);
    this.health = MAX_HEALTH;
    this.air = MAX_AIR;
    this.foodStats.reset();
    this.sinceDamage = Infinity;
    this.drownTimer = 0;
    this.hurtFlash = 0;
    this.flying = false;
    this.blocking = false;
  }

  /**
   * Apply damage under survival rules. Creative (and already-dead players) are
   * invulnerable, so this is always safe to call.
   * Sword blocking reduces incoming attack/projectile damage by 50% (Minecraft 1.8.8 formula: (1.0 + amount) * 0.5).
   * Equipped armor reduces incoming damage by 4% per defense point ((25 - defense) / 25.0).
   */
  hurt(amount: number, cause: string, armorDefense = 0): void {
    if (!this.survival || amount <= 0 || this.isDead) return;
    let finalAmount = amount;
    if (cause !== "drowned" && cause !== "fell from a high place" && cause !== "starved") {
      if (this.blocking) {
        finalAmount = Math.max(1, Math.round((1.0 + finalAmount) * 0.5));
      }
      if (armorDefense > 0) {
        const clampedDefense = Math.min(20, Math.max(0, armorDefense));
        const factor = (25 - clampedDefense) / 25.0;
        finalAmount = Math.max(1, Math.round(finalAmount * factor));
      }
    }
    this.health = Math.max(0, this.health - finalAmount);
    this.sinceDamage = 0;
    this.lastCause = cause;
    this.hurtFlash = Math.min(1, this.hurtFlash + 0.7);
    sound.playPlayerHurt();
  }

  /** Restore health immediately; returns the amount actually healed. */
  heal(amount: number): number {
    if (!Number.isFinite(amount) || amount <= 0 || this.isDead) return 0;
    const before = this.health;
    this.health = Math.min(MAX_HEALTH, this.health + amount);
    return this.health - before;
  }

  /** Feed this frame's input. `dt` is real elapsed seconds. */
  update(input: Input, dt: number): void {
    this.applyLook(input);

    // Double-tap space toggles fly — but only where flight is allowed.
    if (input.wasPressed("Space")) {
      const now = performance.now();
      if (this.allowFlight && now - this.lastSpaceTap < 300) {
        this.flying = !this.flying;
        this.velocity.y = 0;
        this.lastSpaceTap = -1;
      } else {
        this.lastSpaceTap = now;
        this.jumpBuffer = 0.15;
      }
    }
    if (input.wasPressed("KeyF") && this.allowFlight) {
      this.flying = !this.flying;
      this.velocity.y = 0;
    }
    if (!this.allowFlight && this.flying) this.flying = false;

    this.sneaking = !this.flying && input.isDown("ShiftLeft");

    // Sample the medium at the feet and at the eye.
    const feetBlock = this.world.getBlock(
      Math.floor(this.position.x),
      Math.floor(this.position.y + 0.1),
      Math.floor(this.position.z),
    );
    const eyeBlock = this.world.getBlock(
      Math.floor(this.position.x),
      Math.floor(this.position.y + this.eyeHeight),
      Math.floor(this.position.z),
    );
    const wasInWater = this.inWater;
    this.inWater = feetBlock === WATER;
    this.headInWater = eyeBlock === WATER;

    if (!wasInWater && this.inWater) {
      sound.playWaterSplash(true);
    } else if (wasInWater && !this.inWater) {
      sound.playWaterSplash(false);
    }

    // Walk speed along the camera's yaw. `forward` is (-sin, 0, -cos) and
    // `right` is (cos, 0, -sin) for the YXZ camera order used here.
    const move = new THREE.Vector3();
    if (input.isDown("KeyW")) move.z -= 1;
    if (input.isDown("KeyS")) move.z += 1;
    if (input.isDown("KeyA")) move.x -= 1;
    if (input.isDown("KeyD")) move.x += 1;

    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    const worldDir = new THREE.Vector3(
      move.x * cos + move.z * sin,
      0,
      -move.x * sin + move.z * cos,
    );
    if (worldDir.lengthSq() > 0) worldDir.normalize();

    this.sprinting =
      input.isDown("ControlLeft") &&
      move.z < 0 &&
      !this.sneaking &&
      !this.blocking &&
      (!this.survival || this.foodStats.canSprint());

    // Fixed-step integration, capped so a long stall can't spiral.
    this.accumulator = Math.min(this.accumulator + dt, FIXED_DT * MAX_STEPS);
    while (this.accumulator >= FIXED_DT) {
      this.accumulator -= FIXED_DT;
      this.step(FIXED_DT, worldDir, input);
    }

    // Footstep cadence and swimming audio
    const hSpeed = Math.hypot(this.velocity.x, this.velocity.z);
    if (this.onGround && !this.inWater && hSpeed > 0.15) {
      this.stepDistance += hSpeed * dt;
      const stepInterval = this.sprinting ? 2.0 : 2.5;
      if (this.stepDistance >= stepInterval) {
        this.stepDistance = 0;
        const blockBelow = this.world.getBlock(
          Math.floor(this.position.x),
          Math.floor(this.position.y - 0.2),
          Math.floor(this.position.z),
        );
        sound.playFootstep(blockBelow, this.sprinting);
      }
    } else if (this.inWater && hSpeed > 0.15) {
      this.swimTimer += dt;
      if (this.swimTimer >= 0.7) {
        this.swimTimer = 0;
        sound.playWaterSplash(false);
      }
    } else {
      this.stepDistance = Math.min(this.stepDistance, 1.0);
    }

    if (this.survival) this.updateVitals(dt);
    this.hurtFlash = Math.max(0, this.hurtFlash - dt * 2.5);

    this.updateCamera();
  }

  /**
   * Air and health over time. Runs in survival only: air drains underwater and
   * drowning starts once it empties; health slowly regenerates after a quiet
   * spell, so a single fall isn't permanent.
   */
  private updateVitals(dt: number): void {
    this.sinceDamage += dt;

    if (this.headInWater) {
      this.air = Math.max(0, this.air - dt);
      if (this.air <= 0) {
        this.drownTimer += dt;
        if (this.drownTimer >= DROWN_INTERVAL) {
          this.drownTimer = 0;
          this.hurt(1, "drowned");
        }
      }
    } else {
      this.drownTimer = 0;
      if (this.air < MAX_AIR) this.air = Math.min(MAX_AIR, this.air + dt * 4);
    }

    this.foodStats.update(this.survival, dt, this, this.sinceDamage >= REGEN_DELAY ? this.sinceDamage : 0);
  }

  private applyLook(input: Input): void {
    if (!input.locked) return;
    const sensitivity = 0.0022 * this.mouseSensitivity;
    this.yaw -= input.mouse.dx * sensitivity;
    this.pitch -= input.mouse.dy * sensitivity;
    const limit = Math.PI / 2 - 0.001;
    this.pitch = Math.max(-limit, Math.min(limit, this.pitch));
    if (this.yaw > Math.PI) this.yaw -= Math.PI * 2;
    if (this.yaw < -Math.PI) this.yaw += Math.PI * 2;
  }

  private step(dt: number, wishDir: THREE.Vector3, input: Input): void {
    const targetHeight = this.sneaking ? SNEAK_HEIGHT : PLAYER_HEIGHT;
    // Only stand back up if there is room.
    if (targetHeight > this.height && this.fits(this.position, targetHeight)) {
      this.height = targetHeight;
    } else if (targetHeight < this.height) {
      this.height = targetHeight;
    }

    if (this.flying) {
      let speed = input.isDown("ControlLeft") ? FLY_SPRINT_SPEED : FLY_SPEED;
      const vertical = new THREE.Vector3();
      if (input.isDown("Space")) vertical.y += 1;
      if (input.isDown("ShiftLeft")) vertical.y -= 1;

      const target = wishDir.clone().multiplyScalar(speed).add(vertical.multiplyScalar(speed));
      this.velocity.lerp(target, Math.min(1, dt * 12));
      this.onGround = false;
      this.moveWithCollisions(dt, false);
      return;
    }

    const speed = this.inWater
      ? SWIM_SPEED
      : this.sneaking || this.blocking
        ? SNEAK_SPEED
        : this.sprinting
          ? SPRINT_SPEED
          : WALK_SPEED;
    const accel = this.onGround ? GROUND_ACCEL : AIR_ACCEL;

    // Preserve the momentum the player already has; steer toward the target.
    const targetX = wishDir.x * speed;
    const targetZ = wishDir.z * speed;
    const blend = Math.min(1, (accel * dt) / Math.max(speed, 0.001));
    this.velocity.x += (targetX - this.velocity.x) * blend;
    this.velocity.z += (targetZ - this.velocity.z) * blend;

    if (this.inWater) {
      this.velocity.y -= GRAVITY * 0.32 * dt;
      this.velocity.y *= 0.86; // heavy drag
      if (input.isDown("Space")) this.velocity.y = 3.4;
    } else {
      this.velocity.y -= GRAVITY * dt;
      if (this.velocity.y < -TERMINAL_VELOCITY) this.velocity.y = -TERMINAL_VELOCITY;
    }

    // Jump, with coyote time and an input buffer so it feels responsive.
    if (this.jumpBuffer > 0) this.jumpBuffer -= dt;
    if (this.coyote > 0) this.coyote -= dt;
    if (this.jumpBuffer > 0 && (this.onGround || this.coyote > 0 || this.inWater)) {
      this.velocity.y = this.inWater ? 3.4 : JUMP_SPEED;
      this.jumpBuffer = 0;
      this.coyote = 0;
      this.onGround = false;
      this.fallPeak = this.position.y;
      if (this.survival) {
        this.foodStats.addExhaustion(this.sprinting ? 0.8 : 0.2);
      }
    }

    // Ground contact is re-derived every step from this step's collisions, so
    // walking off a ledge stops reading as "grounded". Track the highest point
    // of the current airtime at the same time, for fall damage on touchdown.
    if (this.onGround || this.inWater) this.fallPeak = this.position.y;
    else this.fallPeak = Math.max(this.fallPeak, this.position.y);
    const wasGrounded = this.onGround;
    this.onGround = false;

    // Cobweb slowdown: in Minecraft Java Edition, cobwebs reduce horizontal speed to 25%
    // and vertical descent to 5%, while resetting fall distance to eliminate fall damage.
    const inWeb = this.touchesBlock(COBWEB);
    if (inWeb) {
      this.velocity.x *= 0.25;
      this.velocity.z *= 0.25;
      if (this.velocity.y < 0) {
        this.velocity.y *= 0.05;
      }
      this.fallPeak = this.position.y;
    }

    this.moveWithCollisions(dt, wasGrounded);

    if (this.survival) {
      const hDist = Math.hypot(this.velocity.x, this.velocity.z) * dt;
      if (hDist > 0.001) {
        if (this.inWater) {
          this.foodStats.addExhaustion(0.015 * hDist);
        } else if (this.sprinting) {
          this.foodStats.addExhaustion(0.1 * hDist);
        } else {
          this.foodStats.addExhaustion(0.01 * hDist);
        }
      }
    }

    if (!wasGrounded && this.onGround && !this.inWater) {
      const fallen = Math.max(0, this.fallPeak - this.position.y);
      if (fallen > 0.45) {
        sound.playFallLanding(fallen);
      }
      if (this.survival && fallen > FALL_DAMAGE_THRESHOLD) {
        this.hurt(Math.floor(fallen - FALL_DAMAGE_THRESHOLD), "fell from a high place");
      }
    }
  }

  /**
   * Move one axis at a time and resolve against the voxel grid. Splitting by axis
   * means sliding along walls instead of sticking to them, which is what makes
   * movement feel right.
   */
  private moveWithCollisions(dt: number, wasGrounded: boolean): void {
    let dx = this.velocity.x * dt;
    const dy = this.velocity.y * dt;
    let dz = this.velocity.z * dt;

    const checkLedge =
      this.sneaking &&
      wasGrounded &&
      this.velocity.y <= 0 &&
      !this.flying &&
      !this.inWater &&
      this.hasGroundBelow(this.position);

    if (checkLedge && dx !== 0) {
      const clampedX = this.clampLedgeMovement(dx, 0);
      if (clampedX !== dx) this.velocity.x = 0;
      dx = clampedX;
    }
    this.moveAxis(0, dx);

    this.moveAxis(1, dy);

    if (checkLedge && dz !== 0) {
      const clampedZ = this.clampLedgeMovement(dz, 2);
      if (clampedZ !== dz) this.velocity.z = 0;
      dz = clampedZ;
    }
    this.moveAxis(2, dz);
  }

  /**
   * Ledge protection while sneaking: binary searches safe displacement so a
   * crouching player cannot walk off a cliff.
   */
  private clampLedgeMovement(delta: number, axis: 0 | 2): number {
    const testPos = this.position.clone();
    if (axis === 0) testPos.x += delta;
    else testPos.z += delta;

    if (this.hasGroundBelow(testPos)) return delta;

    let safe = 0;
    let unsafe = delta;
    for (let i = 0; i < 8; i++) {
      const mid = (safe + unsafe) / 2;
      if (axis === 0) testPos.x = this.position.x + mid;
      else testPos.z = this.position.z + mid;

      if (this.hasGroundBelow(testPos)) {
        safe = mid;
      } else {
        unsafe = mid;
      }
    }
    return safe;
  }

  /** Checks whether any solid block supports the player's footprint immediately below pos.y. */
  private hasGroundBelow(pos: THREE.Vector3): boolean {
    const half = PLAYER_WIDTH / 2;
    const minX = Math.floor(pos.x - half + COLL_EPS);
    const maxX = Math.floor(pos.x + half - COLL_EPS);
    const minZ = Math.floor(pos.z - half + COLL_EPS);
    const maxZ = Math.floor(pos.z + half - COLL_EPS);
    const belowY = Math.floor(pos.y - 0.1);
    if (belowY < 0) return true;
    if (belowY >= WORLD_HEIGHT) return false;

    for (let z = minZ; z <= maxZ; z++) {
      for (let x = minX; x <= maxX; x++) {
        if (isSolid(this.world.peekBlock(x, belowY, z))) return true;
      }
    }
    return false;
  }

  private moveAxis(axis: 0 | 1 | 2, delta: number): void {
    if (delta === 0) return;
    const steps = Math.max(1, Math.ceil(Math.abs(delta) / MAX_SUBSTEP));
    const inc = delta / steps;
    const key = axis === 0 ? "x" : axis === 1 ? "y" : "z";
    // Vertical box spans [pos, pos + height]; horizontal spans [pos - half, pos + half].
    const back = axis === 1 ? 0 : PLAYER_WIDTH / 2;
    const fwd = axis === 1 ? this.height : PLAYER_WIDTH / 2;

    for (let s = 0; s < steps; s++) {
      this.position[key] += inc;
      if (!this.intersectsWorld()) continue;

      // Snap flush against the face we penetrated and kill the velocity.
      const lo = this.position[key] - back;
      const hi = this.position[key] + fwd;
      if (inc > 0) {
        // Leading edge is inside block floor(hi): pull it back to that block's near face.
        this.position[key] = Math.floor(hi) - fwd - EPS;
      } else {
        // Trailing edge is inside block floor(lo): push it to that block's far face.
        this.position[key] = Math.floor(lo) + 1 + back + EPS;
        if (axis === 1) {
          this.onGround = true;
          this.coyote = 0.12;
        }
      }

      if (axis === 1) this.velocity.y = 0;
      else this.velocity[axis === 0 ? "x" : "z"] = 0;
    }
  }

  /** Does the AABB at the current position overlap any solid block? */
  private intersectsWorld(): boolean {
    return this.boxHits(this.position, this.height);
  }

  private fits(pos: THREE.Vector3, height: number): boolean {
    return !this.boxHits(pos, height);
  }

  private boxHits(pos: THREE.Vector3, height: number): boolean {
    const half = PLAYER_WIDTH / 2;
    const minX = Math.floor(pos.x - half + COLL_EPS);
    const maxX = Math.floor(pos.x + half - COLL_EPS);
    const minY = Math.floor(pos.y + COLL_EPS);
    const maxY = Math.floor(pos.y + height - COLL_EPS);
    const minZ = Math.floor(pos.z - half + COLL_EPS);
    const maxZ = Math.floor(pos.z + half - COLL_EPS);

    for (let y = minY; y <= maxY; y++) {
      if (y < 0) return true; // world floor
      if (y >= WORLD_HEIGHT) continue; // open sky
      for (let z = minZ; z <= maxZ; z++) {
        for (let x = minX; x <= maxX; x++) {
          if (isSolid(this.world.peekBlock(x, y, z))) return true;
        }
      }
    }
    return false;
  }

  private touchesBlock(blockId: number): boolean {
    const half = PLAYER_WIDTH / 2;
    const minX = Math.floor(this.position.x - half + COLL_EPS);
    const maxX = Math.floor(this.position.x + half - COLL_EPS);
    const minY = Math.floor(this.position.y + COLL_EPS);
    const maxY = Math.floor(this.position.y + this.height - COLL_EPS);
    const minZ = Math.floor(this.position.z - half + COLL_EPS);
    const maxZ = Math.floor(this.position.z + half - COLL_EPS);

    for (let y = minY; y <= maxY; y++) {
      if (y < 0 || y >= WORLD_HEIGHT) continue;
      for (let z = minZ; z <= maxZ; z++) {
        for (let x = minX; x <= maxX; x++) {
          if (this.world.peekBlock(x, y, z) === blockId) return true;
        }
      }
    }
    return false;
  }

  private updateCamera(): void {
    this.syncCamera();
  }

  /**
   * Push position and orientation onto the shared camera. Called on spawn as
   * well as every update, so the first rendered frame is already correct.
   * Handles first-person, third-person back, and third-person front perspectives
   * with camera obstacle collision avoidance.
   */
  syncCamera(dt = 0): void {
    if (this.fovEffects && this.baseFov) {
      const targetFov = this.sprinting ? this.baseFov * 1.1 : this.baseFov;
      if (Math.abs(this.camera.fov - targetFov) > 0.05) {
        const step = dt > 0 ? Math.min(1, dt * 10) : 1;
        this.camera.fov += (targetFov - this.camera.fov) * step;
        this.camera.updateProjectionMatrix();
      }
    }

    const cp = Math.cos(this.pitch);
    const sp = Math.sin(this.pitch);
    const cy = Math.cos(this.yaw);
    const sy = Math.sin(this.yaw);
    this.forward.set(-sy * cp, sp, -cy * cp);

    const eyeX = this.position.x;
    const eyeY = this.position.y + this.eyeHeight;
    const eyeZ = this.position.z;

    if (this.cameraMode === "first-person") {
      this.camera.position.set(eyeX, eyeY, eyeZ);
      this.camera.rotation.set(this.pitch, this.yaw, 0);
      return;
    }

    const maxDist = 4.0;
    const eye = new THREE.Vector3(eyeX, eyeY, eyeZ);

    if (this.cameraMode === "third-person-back") {
      const backDir = new THREE.Vector3(sy * cp, -sp, cy * cp);
      const hit = this.world.raycast(eye, backDir, maxDist, (id) => isSolid(id));
      const dist = hit ? Math.max(0.4, hit.distance - 0.25) : maxDist;

      this.camera.position.copy(eye).addScaledVector(backDir, dist);
      this.camera.rotation.set(this.pitch, this.yaw, 0);
      return;
    }

    if (this.cameraMode === "third-person-front") {
      const frontDir = this.forward.clone();
      const hit = this.world.raycast(eye, frontDir, maxDist, (id) => isSolid(id));
      const dist = hit ? Math.max(0.4, hit.distance - 0.25) : maxDist;

      this.camera.position.copy(eye).addScaledVector(frontDir, dist);
      this.camera.rotation.set(-this.pitch, this.yaw + Math.PI, 0);
      return;
    }
  }
}
